import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { hydrateStoreMeta } from "../lib/storeMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const StoreMetaContext = createContext({ version: 0, ready: false, isDemo: false });

/**
 * Fetches the tenant-aware store registry (GET /api/store-codes) whenever the
 * signed-in identity changes and hydrates lib/storeMeta in place. `version`
 * bumps on every successful hydrate so the routed subtree can be remounted
 * (via key) — that forces every component to re-read the fresh registry, so a
 * demo session never shows a real store code/name/colour and vice-versa.
 * `isDemo` drives demo-only UX (hide gamification, Domi-Vision, the tutorial).
 */
export function StoreMetaProvider({ children }) {
  const { sessionToken, storeRole, adminLevel, checking } = useAuth();
  const [version, setVersion] = useState(0);
  const [isDemo, setIsDemo] = useState(false);

  useEffect(() => {
    if (checking) return undefined;
    let cancelled = false;
    axios
      .get(`${API}/store-codes`)
      .then((r) => {
        if (cancelled) return;
        hydrateStoreMeta(r.data);
        setIsDemo(!!r.data?.is_demo);
        setVersion((v) => v + 1);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [sessionToken, storeRole?.code, adminLevel, checking]);

  return (
    <StoreMetaContext.Provider value={{ version, ready: version > 0, isDemo }}>
      {children}
    </StoreMetaContext.Provider>
  );
}

export function useStoreMeta() {
  return useContext(StoreMetaContext);
}
